import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, Download, Loader2 } from "lucide-react";

const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];

// bugünden `delta` ay sonrası/öncesi, "YYYY-MM"
const shiftPeriod = (delta) => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + delta);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function GibTahakkukPanel() {
  const [acct, setAcct] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ kullanici_kodu: "", sifre: "" });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [captcha, setCaptcha] = useState(null); // {challenge_id, captcha_image, message}
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);
  const [rows, setRows] = useState(null);
  const [period, setPeriod] = useState(() => shiftPeriod(-1));
  const [py, pm] = period.split("-");
  const thisYear = new Date().getFullYear();

  const poll = async (jobId) => {
    setRunning(true);
    for (let i = 0; i < 100; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data } = await api.get(`/gib/tahakkuk/job/${jobId}`);
        if (data.state === "done") {
          setResult(data.result); setRunning(false);
          if (data.result?.status === "EBEYAN_TAHAKKUK") loadRows(data.result.period);
          return;
        }
      } catch { break; }
    }
    setRunning(false);
    toast.error("İşlem sonucu alınamadı");
  };

  const loadAcct = () => api.get("/gib/dvd-account").then((r) => setAcct(r.data)).catch(() => {});
  useEffect(() => { loadAcct(); }, []);

  const [onlyPending, setOnlyPending] = useState(true);
  const loadRows = (p) => api.get("/gib/overview", { params: { period: p } })
    .then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { loadRows(period); }, [period]);

  const saveAcct = async () => {
    if (!form.kullanici_kodu.trim() || !form.sifre) return toast.error("Kullanıcı kodu ve şifre zorunlu");
    try {
      await api.put("/gib/dvd-account", form);
      toast.success("Bilgiler şifrelenerek kaydedildi");
      setEditing(false); setForm({ kullanici_kodu: "", sifre: "" }); setShowPw(false); loadAcct();
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
  };

  const start = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/gib/tahakkuk/start-login", { period });
      if (data.status === "CAPTCHA_REQUIRED") { setCaptcha(data); setCode(""); }
      else setResult(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Başlatılamadı"); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    if (!code.trim()) return toast.error("Doğrulama kodunu yazın");
    setBusy(true);
    try {
      const { data } = await api.post("/gib/tahakkuk/submit-captcha", { challenge_id: captcha.challenge_id, captcha_code: code.trim() });
      if (data.status === "CAPTCHA_HATALI") {
        setCaptcha({ ...captcha, captcha_image: data.captcha_image || captcha.captcha_image, message: data.message });
        setCode("");
      } else if (data.status === "ISLEM_BASLADI") {
        setCaptcha(null); poll(data.job_id);
      } else { setCaptcha(null); setResult(data); }
    } catch (e) {
      toast.error(e.response?.data?.detail || "İşlem başarısız");
      setCaptcha(null);
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-5 space-y-4" data-testid="gib-tahakkuk-panel">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 font-medium text-sm">
          <Download size={16} /> e-Beyanname Tahakkuk (Dijital Vergi Dairesi girişi ile)
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline" data-testid="gib-acct-edit" onClick={() => setEditing(true)}>
            <KeyRound size={14} className="mr-1.5" />{acct?.status === "Kayıtlı" ? "Erişim Bilgisini Güncelle" : "Erişim Bilgisi Gir"}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-secondary/30 p-3" data-testid="gib-period-picker">
        <span className="text-sm font-medium">Çekilecek dönem:</span>
        <select data-testid="gib-tahakkuk-month" value={pm} onChange={(e) => setPeriod(`${py}-${e.target.value}`)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm font-medium">
          {AYLAR.map((m, i) => <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>)}
        </select>
        <select data-testid="gib-tahakkuk-year" value={py} onChange={(e) => setPeriod(`${e.target.value}-${pm}`)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm font-medium">
          {[0, 1, 2, 3].map((i) => String(thisYear - i)).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <button type="button" data-testid="gib-period-prev" onClick={() => setPeriod(shiftPeriod(-1))}
          className="text-xs px-2.5 py-1 rounded-full border border-border hover:bg-accent">Önceki ay</button>
        <button type="button" data-testid="gib-period-now" onClick={() => setPeriod(shiftPeriod(0))}
          className="text-xs px-2.5 py-1 rounded-full border border-border hover:bg-accent">Bu ay</button>
        <Button size="sm" className="ml-auto" data-testid="gib-tahakkuk-start" onClick={start}
          disabled={busy || running || acct?.status !== "Kayıtlı"}>
          {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}
          {AYLAR[parseInt(pm, 10) - 1]} {py} Tahakkuklarını Çek
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Kendi Dijital Vergi Dairesi kullanıcı kodu/şifreniz şifrelenerek saklanır. Her çekişte ekranda çıkan doğrulama kodunu (captcha) siz yazarsınız.
        Durum: <b>{acct?.status || "..."}</b>
      </p>

      {running && (
        <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm flex items-center gap-2" data-testid="gib-tahakkuk-running">
          <Loader2 size={16} className="animate-spin" /> Giriş başarılı. e-Beyanname'den veriler çekiliyor, bu işlem 1-2 dakika sürebilir. Sayfayı kapatmayın...
        </div>
      )}
      {result && (
        <div className="rounded-lg border border-border bg-secondary/40 p-3 text-sm space-y-1" data-testid="gib-tahakkuk-result">
          <div><b>{result.status}</b> — {result.message}</div>
          {result.screens && (
            <ul className="text-xs text-muted-foreground list-disc pl-5">
              {result.screens.map((s, i) => <li key={i}>{s.title || "(başlıksız)"} · {s.url}</li>)}
            </ul>
          )}
        </div>
      )}

      {rows && (() => {
        const BADGE = {
          "Onaylandı": "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400",
          "Onay Bekliyor": "bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
          "Hatalı": "bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400",
          "Verilmedi": "bg-rose-100 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400",
          "Çekilmedi": "bg-slate-100 text-slate-600 dark:bg-slate-500/10 dark:text-slate-400",
          "İptal Edildi": "bg-zinc-200 text-zinc-700 dark:bg-zinc-500/10 dark:text-zinc-400",
          "Durum Belirsiz": "bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-400",
        };
        const KPI = ["Verilmedi", "Hatalı", "Onay Bekliyor", "Onaylandı"];
        const s = rows.summary || {};
        const shown = onlyPending ? rows.rows.filter((r) => r.status !== "Onaylandı") : rows.rows;
        const todayStr = new Date().toISOString().slice(0, 10);
        return (
          <div className="space-y-3" data-testid="gib-overview">
            <div className="text-xs text-muted-foreground">
              Dönem: <b>{AYLAR[parseInt(pm, 10) - 1]} {py}</b> ·
              Son çekim: <b>{rows.last_fetched_at ? new Date(rows.last_fetched_at).toLocaleString("tr-TR") : "bu dönem için henüz çekilmedi"}</b>
            </div>
            {!rows.fetched && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-500/10 p-3 text-sm" data-testid="gib-not-fetched">
                Bu dönem için GİB'den henüz veri çekilmedi. Aşağıdaki liste, mükelleflerin <b>olması gereken</b> beyannameleridir;
                durumları görmek için yukarıdan <b>"Tahakkuklarını Çek"</b>'e basın.
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {KPI.map((k) => (
                <div key={k} className="rounded-lg border border-border p-3" data-testid={`gib-kpi-${k}`}>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground">{k === "Verilmedi" && !rows.fetched ? "Çekilmedi" : k}</div>
                  <div className="font-head text-2xl font-semibold mt-1">{k === "Verilmedi" && !rows.fetched ? (s["Çekilmedi"] || 0) : (s[k] || 0)}</div>
                </div>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer w-fit">
              <input type="checkbox" checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} data-testid="gib-only-pending" />
              Sadece onaylanmayanları göster
            </label>
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead className="bg-secondary/60 text-xs uppercase text-muted-foreground"><tr>
                  <th className="text-left px-3 py-2 font-medium">Mükellef</th>
                  <th className="text-left px-3 py-2 font-medium">VKN/TCKN</th>
                  <th className="text-left px-3 py-2 font-medium">Beyanname</th>
                  <th className="text-left px-3 py-2 font-medium">Son Tarih</th>
                  <th className="text-left px-3 py-2 font-medium">Durum</th>
                  <th className="text-left px-3 py-2 font-medium">Vergi Tahakkuku</th>
                  <th className="text-left px-3 py-2 font-medium">SGK Tahakkuku</th>
                  <th className="text-left px-3 py-2 font-medium">Yükleme</th>
                </tr></thead>
                <tbody>
                  {shown.map((r, i) => (
                    <tr key={`${r.client_id}-${r.type}-${i}`} className="border-t border-border">
                      <td className="px-3 py-2 font-medium">{r.unvan}</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.vkn}</td>
                      <td className="px-3 py-2">{r.type}</td>
                      <td className={`px-3 py-2 whitespace-nowrap ${r.deadline && r.deadline < todayStr && r.status !== "Onaylandı" ? "text-rose-600 font-medium" : "text-muted-foreground"}`}>
                        {r.deadline ? r.deadline.split("-").reverse().join(".") : "—"}
                        {r.deadline && r.deadline < todayStr && r.status !== "Onaylandı" ? " · gecikti" : ""}
                      </td>
                      <td className="px-3 py-2"><span className={`text-xs px-2 py-0.5 rounded ${BADGE[r.status] || "bg-secondary"}`}>{r.status}</span></td>
                      <td className="px-3 py-2 text-muted-foreground">{r.vergi || "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{r.sgk || "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.yukleme || "—"}</td>
                    </tr>
                  ))}
                  {shown.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">
                      {onlyPending ? "Onaylanmamış beyanname yok." : "Bu dönem için beyanname yok."}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {rows.unmatched && rows.unmatched.length > 0 && (
              <div className="text-xs text-muted-foreground" data-testid="gib-unmatched">
                <b>Uygulamada karşılığı olmayan GİB kayıtları ({rows.unmatched.length}):</b>{" "}
                {rows.unmatched.map((u) => `${u.unvan || u.vkn} · ${u.type} · ${u.status}`).join(" | ")}
              </div>
            )}
          </div>
        );
      })()}

      <Dialog open={editing} onOpenChange={setEditing}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Dijital Vergi Dairesi Erişim Bilgileri</DialogTitle>
            <DialogDescription>Mali müşavir olarak kendi giriş bilgileriniz. Şifrelenerek saklanır.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1"><Label className="text-xs">Kullanıcı Kodu</Label>
              <Input data-testid="gib-acct-kod" value={form.kullanici_kodu} autoComplete="off" onChange={(e) => setForm({ ...form, kullanici_kodu: e.target.value })} />
            </div>
            <div className="space-y-1"><Label className="text-xs">Şifre</Label>
              <div className="relative">
                <Input data-testid="gib-acct-sifre" type={showPw ? "text" : "password"} className="pr-9" value={form.sifre} autoComplete="new-password" onChange={(e) => setForm({ ...form, sifre: e.target.value })} />
                <button type="button" tabIndex={-1} onClick={() => setShowPw((v) => !v)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(false)}>İptal</Button>
            <Button data-testid="gib-acct-save" onClick={saveAcct}>Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!captcha} onOpenChange={(v) => !v && setCaptcha(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Doğrulama Kodu</DialogTitle>
            <DialogDescription>Resimdeki kodu yazın. Süre yaklaşık 3 dakikadır.</DialogDescription>
          </DialogHeader>
          {captcha?.message && <p className="text-xs text-rose-600">{captcha.message}</p>}
          {captcha?.captcha_image && <img src={captcha.captcha_image} alt="captcha" className="border border-border rounded bg-white max-h-24" />}
          <Input data-testid="gib-captcha-input" value={code} autoComplete="off" onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Doğrulama kodu" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCaptcha(null)}>Vazgeç</Button>
            <Button data-testid="gib-captcha-submit" onClick={submit} disabled={busy}>
              {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null} Giriş Yap
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
